import React, { useContext, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import {
  urls,
  useAsyncEffect,
  useJ1AuthFetch,
  useAccounts,
} from '@jupiterone/jupiter-web-toolkit/src';
import { FeatureFlags } from '@jupiterone/jupiter-web-toolkit/src/types';

type TFeatureFlagsContext = {
  loading: boolean;
  hasFeature: (...flags: Array<keyof FeatureFlags>) => boolean;
};

interface Props {
  children: any;
}

const DEFAULT_ALL_FLAGS_FALSE: FeatureFlags = {
  autocomplete: false,
  mfaConfiguration: false,
  downloadPolicies: false,
  deferHomeSearchQueries: false,
  questionTrendCharts: false,
  addQuestionToDashboard: false,
  complianceSummary: false,
  queryPermissions: false,
  enableAuthClientCreation: false,
  streamedRuleEvaluations: false,
  criticalAssets: false,
  j1EvaluateAllQuestions: false,
  complianceReviews: false,
  queryExecutionV2: false,
  enableSampleData: false,
  policiesFullTextSearch: false,
  compliancev15: false,
  notifications: false,
  accountLogo: false,
  accountLogoSettings: false,
  navSearchAnywhere: false,
  linksToTheCommunity: false,
  landingPageRefresh: false,
  complianceV2: false,
};

export const FeatureFlagsContext = React.createContext<TFeatureFlagsContext>({
  loading: false,
  hasFeature: () => false,
});

export const FeatureFlagsProvider = ({ children }: Props) => {
  const { authFetch } = useJ1AuthFetch();
  const { account } = useAccounts();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    DEFAULT_ALL_FLAGS_FALSE
  );

  const loading = useBooleanState(true);

  const hasFeature = (...flags: Array<keyof FeatureFlags>) => {
    return flags.every((flag) => featureFlags[flag] === true);
  };

  useAsyncEffect(async () => {
    if (!account) {
      loading.setFalse();
      return;
    }
    const featureFlagsData: FeatureFlags = await authFetch(
      urls.featureFlags + '?project=JupiterOne',
      {
        method: 'GET',
      }
    ).then((resp) => resp.json());

    setFeatureFlags(featureFlagsData);
    loading.setFalse();
  }, [account]);

  return (
    <FeatureFlagsContext.Provider
      value={{
        loading: loading.state,
        hasFeature,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
